import { VectorFeatureControl } from "../controls/VectorFeatureControl";
import { RevisionControl } from "../controls/RevisionControl";
import {
  buildRevisionParams,
  fetchRevisions,
} from "../services/RevisionService";
import { getWFSLayer } from "../layers/WFSLayer";
import { getHoverLayer } from "../layers/HoverLayer";
import {
  getLayersFromURL,
  getLayerSlug,
  getRevisionsFromURL,
  registerLayerControl,
  registerRevisionControl,
} from "../state/URLStateManager";

export function addWFSLayerAndControl({
  layerName,
  url,
  role,
  style,
  azureLoginControl,
  featureInfoControl,
  div,
  map,
  getInteractions,
  featureInfoHandlers,
  hoverStyleFunctions,
  addFeatureHandlers = [],
}) {
  const slug = getLayerSlug(layerName);

  const control = new VectorFeatureControl({
    layer_name: layerName,
    target: div,
  });

  // Restore layer visibility from URL.
  if (getLayersFromURL().includes(slug)) {
    control.setToggle(true);
  }
  registerLayerControl(slug, control);

  // Revision state shared between the control and the layer URL builder
  let currentRevisionParams = [];
  let hasAppliedUrlRevisions = false;

  const revisionControl = new RevisionControl({
    headerEl: control.headerEl,
    bodyEl: control.bodyEl,
  });
  registerRevisionControl(slug, revisionControl);

  // Fetch revisions once the auth token is available (or immediately if already set)
  function tryFetchRevisions() {
    const token = azureLoginControl.get("idToken");
    if (!token) return;
    fetchRevisions(url, token)
      .then((layerRevisions) => {
        let revisionsToUse = layerRevisions;
        let restoredFromUrl = false;

        // Apply URL revision overrides on first load only
        if (!hasAppliedUrlRevisions) {
          hasAppliedUrlRevisions = true;
          const urlRevisions = getRevisionsFromURL();
          const slugRevisions = urlRevisions.get(slug) || [];
          if (slugRevisions.length > 0) {
            restoredFromUrl = true;
            revisionsToUse = layerRevisions.map((lr) => {
              const match = slugRevisions.find((u) => u.layerId === lr.layerId);
              return match ? { ...lr, revision: match.revision } : lr;
            });
            // Check if any revision actually differs from the latest
            const hasOverride = revisionsToUse.some(
              (r, i) => r.revision !== layerRevisions[i].revision,
            );
            if (hasOverride) {
              revisionControl.expand();
            }
          }
        }

        const newRevisionParams = buildRevisionParams(revisionsToUse);
        const changed =
          newRevisionParams.length !== currentRevisionParams.length ||
          newRevisionParams.some((p, i) => p !== currentRevisionParams[i]);

        revisionControl.setRevisions(revisionsToUse);
        currentRevisionParams = newRevisionParams;

        if (restoredFromUrl) {
          // Restored from URL: publish through the RevisionControl (its
          // selectedRevisions handler below forwards to the layer control and
          // refreshes the data). This also lets URLStateManager re-serialize
          // the revisions: the initial pushState runs before this async fetch
          // resolves and would otherwise drop the revision state from the URL.
          revisionControl.set("selectedRevisions", revisionsToUse);
        } else if (changed) {
          control.set("selectedRevisions", revisionsToUse);
        }
      })
      .catch((err) => {
        revisionControl.setError(err);
      });
  }

  // Try immediately; also re-fetch whenever the token changes (login/logout)
  tryFetchRevisions();
  azureLoginControl.on("propertychange", function (e) {
    if (e.key === "idToken") {
      tryFetchRevisions();
    }
  });

  // Re-fetch when the user clicks "Update to latest revisions"
  revisionControl.on("propertychange", function (e) {
    if (e.key === "refreshRequested") {
      revisionControl.setLoading();
      tryFetchRevisions();
    }
  });

  // Keep currentRevisionParams in sync when the user adjusts a revision value
  revisionControl.on("propertychange", function (e) {
    if (e.key === "selectedRevisions") {
      currentRevisionParams = buildRevisionParams(
        revisionControl.get("selectedRevisions"),
      );
      // Forward to VectorFeatureControl so WFSLayer can react
      control.set(
        "selectedRevisions",
        revisionControl.get("selectedRevisions"),
      );
    }
  });

  const wfsUrl = url + "/wfs";
  const layer = getWFSLayer(
    azureLoginControl,
    control,
    wfsUrl,
    role,
    style,
    addFeatureHandlers,
    () => currentRevisionParams,
  );

  map.addControl(control);
  map.addControl(revisionControl);
  map.addLayer(layer);
  hoverStyleFunctions.forEach((hoverStyleFunction) => {
    const hoverLayer = getHoverLayer(hoverStyleFunction);
    map.addLayer(hoverLayer);
    featureInfoControl.addHoverLayerForLayer(hoverLayer, layer);
  });
  featureInfoHandlers.forEach((handler) =>
    featureInfoControl.addHandlerForLayer(handler, layer),
  );

  getInteractions(layer, control).forEach((interaction) =>
    map.addInteraction(interaction),
  );
  map.on("moveend", function () {
    control.set("zoom", map.getView().getZoom());
  });
  azureLoginControl.on("propertychange", function () {
    layer.getSource().refresh();
  });
}
